import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

interface SearchItem {
    chave: string;
    texto: string;
    link: string;
}

interface SearchBoxProps {
    dataSource: SearchItem[];
}

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  padding: 8px 12px 8px 36px; /* Increased left padding for icon */
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 5px;
  outline: none;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: #317eeb;
  }
`;

const SearchIcon = styled.svg`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  fill: #666;
`;

const Dropdown = styled.ul`
  position: fixed;
  width: 300px;
  max-height: 200px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 5px;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  z-index: 100;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #bbb;
  }
`;

const DropdownItem = styled.li<{ isFocused: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  background: ${({ isFocused }) => (isFocused ? '#f0f0f0' : 'white')};

  &:hover {
    background: #f0f0f0;
  }
`;

const HighlightedText = styled.span`
  font-weight: bold;
  color: #317eeb;
`;

const Wrapper = styled.div`
  position: relative;
  max-width: 300px;
  margin: 0 auto;
`;

const normalizeText = (text: string): string => {
    return text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
};

const SearchBox: React.FC<SearchBoxProps> = ({ dataSource }) => {
    const [query, setQuery] = useState('');
    const [filteredItems, setFilteredItems] = useState<SearchItem[]>([]);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const history = useHistory();

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);

        if (value.trim() === '') {
            setFilteredItems([]);
        } else {
            const normalizedQuery = normalizeText(value);
            const results = dataSource.filter((item) =>
                normalizeText(item.texto).includes(normalizedQuery)
            );
            setFilteredItems(results);
            setFocusedIndex(-1);
            setIsDropdownOpen(true);
        }
    };

    const handleItemClick = (link: string) => {
        history.push(link);
        setFilteredItems([]);
        setQuery('');
        setIsDropdownOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (filteredItems.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setFocusedIndex((prev) => (prev + 1) % filteredItems.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedIndex(
                    (prev) => (prev - 1 + filteredItems.length) % filteredItems.length
                );
            } else if (e.key === 'Enter' && focusedIndex >= 0) {
                e.preventDefault();
                handleItemClick(filteredItems[focusedIndex].link);
            }
        }
    };

    const handleFocus = () => {
        if (query.trim() !== '' && filteredItems.length > 0) {
            setIsDropdownOpen(true);
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        const relatedTarget = event.relatedTarget as HTMLElement;
        if (
            wrapperRef.current &&
            relatedTarget &&
            !wrapperRef.current.contains(relatedTarget)
        ) {
            setIsDropdownOpen(false);
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
            setIsDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const highlightMatch = (text: string, query: string) => {
        const normalizedQuery = normalizeText(query);
        const normalizedText = normalizeText(text);
        const startIndex = normalizedText.indexOf(normalizedQuery);

        if (startIndex === -1) return text;

        const endIndex = startIndex + normalizedQuery.length;
        return (
            <>
                {text.slice(0, startIndex)}
                <HighlightedText>{text.slice(startIndex, endIndex)}</HighlightedText>
                {text.slice(endIndex)}
            </>
        );
    };

    return (
        <Wrapper ref={wrapperRef} onBlur={handleBlur}>
            <SearchContainer>
                <SearchIcon viewBox="0 0 24 24">
                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </SearchIcon>
                <SearchInput
                    type="text"
                    value={query}
                    onChange={handleSearch}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    placeholder="Para onde deseja ir? "
                />
            </SearchContainer>
            {isDropdownOpen && (
                <Dropdown>
                    {filteredItems.length > 0 ? (
                        filteredItems.map((item, index) => (
                            <DropdownItem
                                key={item.chave}
                                isFocused={index === focusedIndex}
                                onMouseEnter={() => setFocusedIndex(index)}
                                onClick={() => handleItemClick(item.link)}
                            >
                                {highlightMatch(item.texto, query)}
                            </DropdownItem>
                        ))
                    ) : (
                        <DropdownItem isFocused={false} onClick={() => setIsDropdownOpen(false)}>
                            Nenhum resultado encontrado.
                        </DropdownItem>
                    )}
                </Dropdown>
            )}
        </Wrapper>
    );
};

export default SearchBox;